import React, { useState } from 'react';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingBag, Users, 
  Package, Star, Clock, Eye, Download, Calendar, Filter,
  ChevronLeft, ChevronRight, Zap, Award, Crown, Rocket,
  BarChart3, PieChart as PieChartIcon, LineChart as LineChartIcon, Activity, Target,
  Percent, Heart, ThumbsUp, Share2, Mail, Printer
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart as ReLineChart, Line, ComposedChart
} from 'recharts';
import { Link } from 'react-router-dom';

const Analytics = () => {
  const [timeframe, setTimeframe] = useState('month');
  const [metricType, setMetricType] = useState('revenue');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Key Metrics Data
  const metrics = {
    revenue: { value: 3245000, change: 12.5, target: 2800000, progress: 116 },
    orders: { value: 1342, change: 8.2, target: 1200, progress: 112 },
    customers: { value: 8450, change: 5.1, target: 8000, progress: 106 },
    avgOrderValue: { value: 2418, change: 3.8, target: 2350, progress: 103 },
    conversionRate: { value: 3.2, change: -0.5, target: 3.5, progress: 91 },
    profitMargin: { value: 32.5, change: 2.1, target: 30, progress: 108 },
    returningCustomers: { value: 3420, change: 6.8, target: 3200, progress: 107 },
    customerSatisfaction: { value: 4.7, change: 0.2, target: 4.5, progress: 104 },
  };

  // Chart Data
  const revenueData = [
    { month: 'Jan', revenue: 125000, orders: 45, profit: 87500, customers: 320 },
    { month: 'Feb', revenue: 145000, orders: 62, profit: 101500, customers: 380 },
    { month: 'Mar', revenue: 168000, orders: 78, profit: 117600, customers: 450 },
    { month: 'Apr', revenue: 210000, orders: 92, profit: 147000, customers: 520 },
    { month: 'May', revenue: 245000, orders: 108, profit: 171500, customers: 610 },
    { month: 'Jun', revenue: 230000, orders: 95, profit: 161000, customers: 580 },
    { month: 'Jul', revenue: 267000, orders: 112, profit: 186900, customers: 650 },
    { month: 'Aug', revenue: 289000, orders: 125, profit: 202300, customers: 710 },
    { month: 'Sep', revenue: 278000, orders: 118, profit: 194600, customers: 690 },
    { month: 'Oct', revenue: 312000, orders: 135, profit: 218400, customers: 780 },
    { month: 'Nov', revenue: 345000, orders: 148, profit: 241500, customers: 850 },
    { month: 'Dec', revenue: 420000, orders: 165, profit: 294000, customers: 950 },
  ];

  const categoryPerformance = [
    { name: 'Indoor Plants', revenue: 1125000, growth: 15, percentage: 35, color: '#10b981' },
    { name: 'Outdoor Plants', revenue: 810000, growth: 12, percentage: 25, color: '#3b82f6' },
    { name: 'Flowering Plants', revenue: 648000, growth: 18, percentage: 20, color: '#f59e0b' },
    { name: 'Air Purifying', revenue: 388000, growth: 22, percentage: 12, color: '#8b5cf6' },
    { name: 'Others', revenue: 259000, growth: 8, percentage: 8, color: '#ef4444' },
  ];

  const customerInsights = {
    demographics: [
      { age: '18-24', percentage: 15, color: '#10b981' },
      { age: '25-34', percentage: 35, color: '#3b82f6' },
      { age: '35-44', percentage: 28, color: '#f59e0b' },
      { age: '45-54', percentage: 15, color: '#8b5cf6' },
      { age: '55+', percentage: 7, color: '#ef4444' },
    ],
    segments: [
      { type: 'New Customers', value: 5030, percentage: 60 },
      { type: 'Returning', value: 3420, percentage: 40 },
    ]
  };

  const topProducts = [
    { id: 1, name: 'Monstera Deliciosa', sales: 1245, revenue: 560250, growth: 15, rating: 4.9 },
    { id: 2, name: 'Snake Plant', sales: 987, revenue: 295000, growth: 8, rating: 4.8 },
    { id: 3, name: 'Fiddle Leaf Fig', sales: 876, revenue: 744600, growth: 22, rating: 4.9 },
    { id: 4, name: 'Peace Lily', sales: 765, revenue: 267750, growth: 5, rating: 4.7 },
    { id: 5, name: 'Aloe Vera', sales: 654, revenue: 98100, growth: -2, rating: 4.6 },
  ];

  const seasonalTrends = [
    { season: 'Winter', revenue: 680000, orders: 285, growth: 8 },
    { season: 'Spring', revenue: 890000, orders: 365, growth: 12 },
    { season: 'Summer', revenue: 1020000, orders: 425, growth: 15 },
    { season: 'Fall', revenue: 750000, orders: 312, growth: 10 },
  ];

  const MetricCard = ({ title, metric, icon: Icon, prefix = '₹', suffix = '' }) => (
    <div className="metric-card">
      <div className="metric-header">
        <span className="metric-title">{title}</span>
        <Icon size={18} className="metric-icon" />
      </div>
      <div className="metric-value">
        {prefix}{metric.value.toLocaleString()}{suffix}
      </div>
      <div className="metric-footer">
        <span className={`metric-change ${metric.change >= 0 ? 'positive' : 'negative'}`}>
          {metric.change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {Math.abs(metric.change)}%
        </span>
        <span className="metric-target">Target: {prefix}{metric.target.toLocaleString()}{suffix}</span>
      </div>
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${metric.progress}%`, backgroundColor: metric.progress >= 100 ? '#10b981' : '#3b82f6' }}></div>
      </div>
    </div>
  );

  const MetricGrid = () => {
    const metricsList = [
      { title: 'Total Revenue', metric: metrics.revenue, icon: DollarSign, prefix: '₹' },
      { title: 'Total Orders', metric: metrics.orders, icon: ShoppingBag, prefix: '' },
      { title: 'Total Customers', metric: metrics.customers, icon: Users, prefix: '' },
      { title: 'Avg Order Value', metric: metrics.avgOrderValue, icon: TrendingUp, prefix: '₹' },
      { title: 'Conversion Rate', metric: metrics.conversionRate, icon: Percent, suffix: '%', prefix: '' },
      { title: 'Profit Margin', metric: metrics.profitMargin, icon: Activity, suffix: '%', prefix: '' },
      { title: 'Returning Customers', metric: metrics.returningCustomers, icon: Heart, prefix: '' },
      { title: 'Customer Satisfaction', metric: metrics.customerSatisfaction, icon: Star, suffix: '★', prefix: '' },
    ];

    return (
      <div className="metrics-grid">
        {metricsList.map((item, idx) => (
          <MetricCard key={idx} {...item} />
        ))}
      </div>
    );
  };

  return (
    <div className="admin-analytics-modern">
      {/* Header */}
      <div className="analytics-header">
        <div>
          <h1 className="page-title">Business Analytics</h1>
          <p className="page-subtitle">Deep insights into your business performance</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export Report
          </button>
          <button className="btn-primary">
            <Mail size={16} />
            Schedule Report
          </button>
        </div>
      </div>

      {/* Timeframe Selector */}
      <div className="timeframe-bar">
        <div className="timeframe-buttons">
          <button className={`time-btn ${timeframe === 'week' ? 'active' : ''}`} onClick={() => setTimeframe('week')}>Last 7 Days</button>
          <button className={`time-btn ${timeframe === 'month' ? 'active' : ''}`} onClick={() => setTimeframe('month')}>Last 30 Days</button>
          <button className={`time-btn ${timeframe === 'quarter' ? 'active' : ''}`} onClick={() => setTimeframe('quarter')}>Last Quarter</button>
          <button className={`time-btn ${timeframe === 'year' ? 'active' : ''}`} onClick={() => setTimeframe('year')}>Last Year</button>
          <button className={`time-btn ${timeframe === 'all' ? 'active' : ''}`} onClick={() => setTimeframe('all')}>All Time</button>
        </div>
        <div className="custom-range">
          <Calendar size={16} />
          <span>Custom Range</span>
        </div>
      </div>

      {/* Metrics Grid - 8 Cards */}
      <MetricGrid />

      {/* Revenue Overview Chart */}
      <div className="chart-card large">
        <div className="chart-header">
          <div>
            <h3 className="chart-title">Revenue & Orders Overview</h3>
            <p className="chart-subtitle">Monthly performance trends</p>
          </div>
          <div className="chart-controls">
            <select className="metric-select" value={metricType} onChange={(e) => setMetricType(e.target.value)}>
              <option value="revenue">Revenue</option>
              <option value="orders">Orders</option>
              <option value="profit">Profit</option>
              <option value="customers">Customers</option>
            </select>
          </div>
        </div>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={revenueData}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8ecef" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={(value) => `₹${value/1000}k`} />
              <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px' }}
                formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']}
              />
              <Legend />
              <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" name="Revenue (₹)" />
              <Bar yAxisId="right" dataKey="orders" fill="#3b82f6" radius={[8, 8, 0, 0]} name="Orders" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="two-column-grid">
        {/* Category Performance */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Category Performance</h3>
            <p className="chart-subtitle">Revenue by product category</p>
          </div>
          <div className="pie-chart-container">
            <ResponsiveContainer width="100%" height="250px">
              <PieChart>
                <Pie
                  data={categoryPerformance}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="revenue"
                >
                  {categoryPerformance.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="category-list">
            {categoryPerformance.map((cat, idx) => (
              <div key={idx} className="category-item">
                <span className="category-color" style={{ backgroundColor: cat.color }}></span>
                <span className="category-name">{cat.name}</span>
                <span className="category-revenue">₹{cat.revenue.toLocaleString()}</span>
                <span className={`category-growth ${cat.growth >= 0 ? 'positive' : 'negative'}`}>
                  {cat.growth >= 0 ? '+' : ''}{cat.growth}%
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Products */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Top Performing Products</h3>
            <p className="chart-subtitle">Best sellers by revenue</p>
          </div>
          <div className="top-products-list">
            {topProducts.map((product, idx) => (
              <div key={product.id} className="top-product-item">
                <div className="product-rank">#{idx + 1}</div>
                <div className="product-info">
                  <div className="product-name">{product.name}</div>
                  <div className="product-stats">
                    <span>📦 {product.sales} sold</span>
                    <span>⭐ {product.rating}</span>
                  </div>
                </div>
                <div className="product-revenue">₹{product.revenue.toLocaleString()}</div>
                <div className={`product-growth ${product.growth >= 0 ? 'positive' : 'negative'}`}>
                  {product.growth >= 0 ? '+' : ''}{product.growth}%
                </div>
              </div>
            ))}
          </div>
          <Link to="/admin/products" className="view-all-link">View All Products →</Link>
        </div>
      </div>

      {/* Third Row */}
      <div className="two-column-grid">
        {/* Customer Demographics */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Customer Demographics</h3>
            <p className="chart-subtitle">Age distribution of customers</p>
          </div>
          <div className="demographics-chart">
            <ResponsiveContainer width="100%" height="200px">
              <BarChart data={customerInsights.demographics} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e8ecef" />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <YAxis type="category" dataKey="age" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                <Tooltip />
                <Bar dataKey="percentage" fill="#10b981" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="segment-stats">
            <div className="segment-item">
              <span className="segment-label">New Customers</span>
              <span className="segment-value">{customerInsights.segments[0].value}</span>
              <div className="segment-bar">
                <div className="segment-fill" style={{ width: `${customerInsights.segments[0].percentage}%`, backgroundColor: '#3b82f6' }}></div>
              </div>
            </div>
            <div className="segment-item">
              <span className="segment-label">Returning Customers</span>
              <span className="segment-value">{customerInsights.segments[1].value}</span>
              <div className="segment-bar">
                <div className="segment-fill" style={{ width: `${customerInsights.segments[1].percentage}%`, backgroundColor: '#10b981' }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Seasonal Trends */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Seasonal Trends</h3>
            <p className="chart-subtitle">Performance across seasons</p>
          </div>
          <div className="seasonal-grid">
            {seasonalTrends.map((season, idx) => (
              <div key={idx} className="season-item">
                <div className="season-name">{season.season}</div>
                <div className="season-revenue">₹{season.revenue.toLocaleString()}</div>
                <div className="season-orders">{season.orders} orders</div>
                <div className={`season-growth ${season.growth >= 0 ? 'positive' : 'negative'}`}>
                  {season.growth >= 0 ? '+' : ''}{season.growth}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Key Insights */}
      <div className="insights-card">
        <div className="insights-header">
          <Zap size={20} />
          <h3>Key Insights & Recommendations</h3>
        </div>
        <div className="insights-grid">
          <div className="insight-item">
            <div className="insight-icon">📈</div>
            <div className="insight-content">
              <h4>Revenue Growth Opportunity</h4>
              <p>Your revenue has grown 18.5% this quarter. Consider investing more in top-performing categories like Indoor Plants.</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">👥</div>
            <div className="insight-content">
              <h4>Customer Retention</h4>
              <p>Returning customers have increased by 6.8%. Implement a loyalty program to further boost retention.</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">📦</div>
            <div className="insight-content">
              <h4>Best Seller Performance</h4>
              <p>Fiddle Leaf Fig shows 22% growth. Increase stock levels and marketing focus for this product.</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">🎯</div>
            <div className="insight-content">
              <h4>Conversion Optimization</h4>
              <p>Conversion rate is 3.2%, slightly below target. A/B test your checkout process to improve.</p>
            </div>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default Analytics;